import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Filter, X } from "lucide-react";
import apiService from "../../services/api_services.js";
import auth_service from "../../services/auth_service.js";
import SkeletonLoading from "../../components/SkeletonLoading";
import toast from "react-hot-toast";
import StarRating from "../../components/StarRating";
import { useWishlist } from "../../context/WishlistContext";
import { addToRecentlyViewed } from "../../utils/recentlyViewed";

// Mobile-first styled components with updated colors
const Container = styled.div`
  display: flex;
  // padding: 20px;
  background-color: #f8f9fa;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 24px;
  }
`;
const Sidebar = styled.aside`
  flex: 1;
  padding: 24px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  max-width: 100%;
  border: 1px solid rgba(0, 0, 122, 0.05);
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #1F509A, #000055);
  }
  @media (max-width: 767px) {
    display: none;
  }
  @media (min-width: 768px) {
    margin-bottom: 0;
    max-width: 320px;
    position: sticky;
    top: 20px;
    height: fit-content;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
    /* Improved scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: #1F509A #f0f0f0;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f0f0f0;
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #1F509A;
      border-radius: 10px;
    }
  }
`;
const MobileFilterButton = styled.button`
  display: none;
  background-color: #1F509A;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 122, 0.1);
  transition: all 0.2s ease;
  position: relative;
  margin-left: auto; /* Push to the right */
  @media (max-width: 767px) {
    display: flex;
  }
  &:hover {
    background-color: #000055;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 122, 0.15);
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;
const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 85%;
  height: 100vh;
  background-color: white;
  z-index: 1000;
  transform: translateX(${(props) => (props.isOpen ? "0" : "-100%")});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
  padding: 24px;
  box-shadow: 2px 0 15px rgba(0, 0, 0, 0.15);
`;
const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 999;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  backdrop-filter: blur(3px);
`;
const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #1F509A;
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1F509A;
  transition: transform 0.2s ease;
  &:hover {
    transform: scale(1.1);
  }
`;
const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 2px solid #1F509A;
  padding-bottom: 16px;
`;
const FilterTitle = styled.h2`
  font-size: 1.5rem;
  color: #1F509A;
  font-weight: 700;
`;
const ResetButton = styled.button`
  background-color: #f8f9fa;
  color: #1F509A;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  z-index: 1;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0%;
    height: 100%;
    background-color: #1F509A;
    transition: width 0.3s ease;
    z-index: -1;
  }
  &:hover {
    color: white;
    border-color: #1F509A;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 122, 0.2);
  }
  &:hover:before {
    width: 100%;
  }
  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
  &:hover svg {
    transform: rotate(180deg);
    color: white;
  }
`;
const FilterSection = styled.div`
  margin-bottom: 24px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid #f0f0f0;
  background-color: white;
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 122, 0.08);
    transform: translateY(-2px);
    border-color: rgba(0, 0, 122, 0.1);
  }
  &:last-child {
    margin-bottom: 8px;
  }
`;
const FilterToggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 18px;
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(to bottom, #1F509A, #000055);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  h3 {
    font-size: 1.05rem;
    font-weight: 600;
    color: #1F509A;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: transform 0.3s ease;
  }
  &:hover {
    background-color: #f8f9fa;
    h3 {
      transform: translateX(4px);
    }
    &::before {
      opacity: 1;
    }
  }
  /* Add a subtle animation for active state */
  &:active {
    background-color: #f0f4ff;
    h3 {
      transform: translateX(2px);
    }
  }
`;
const ToggleButton = styled.button`
  background: ${props => props.children === "−" ? "#1F509A" : "white"};
  color: ${props => props.children === "−" ? "white" : "#1F509A"};
  border: 1px solid ${props => props.children === "−" ? "#1F509A" : "#e2e8f0"};
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  &:hover {
    background-color: ${props => props.children === "−" ? "#000055" : "#f8f9fa"};
    transform: scale(1.05);
  }
`;
const FilterOptions = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: white;
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #1F509A #f0f0f0;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #1F509A;
    border-radius: 10px;
  }
`;
const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background-color: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  user-select: none;
  position: relative;
  overflow: hidden;
  margin-bottom: 6px;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, rgba(0, 0, 122, 0.05), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  &:hover {
    border-color: #1F509A;
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 122, 0.06);
    &::after {
      opacity: 1;
    }
    .custom-checkbox {
      border-color: #1F509A;
    }
  }
  &:active {
    transform: translateX(2px);
  }
  input {
    display: none;
  }
  span {
    font-size: 0.9rem;
    color: #4a5568;
    transition: all 0.3s ease;
    letter-spacing: 0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
  }
  .custom-checkbox {
    width: 16px;
    height: 16px;
    border: 2px solid #e2e8f0;
    border-radius: 4px;
    margin-right: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) inset;
  }
  .custom-checkbox.checked {
    background-color: #1F509A;
    border-color: #1F509A;
    box-shadow: 0 2px 6px rgba(0, 0, 122, 0.2);
    & + span {
      color: #1F509A;
      font-weight: 600;
    }
    &::after {
      content: "";
      position: absolute;
      width: 8px;
      height: 4px;
      border-left: 2px solid #FFD700;
      border-bottom: 2px solid #FFD700;
      transform: rotate(-45deg);
      top: 4px;
      animation: checkmark 0.2s ease-in-out;
    }
  }
  .tooltip {
    position: absolute;
    background-color: #1F509A;
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    white-space: nowrap;
    top: -100%;
    left: 50%;
    transform: translateX(-50%) translateY(-10px);
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    &:after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid #1F509A;
    }
  }
  .tooltip.visible {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(-5px);
  }
  @keyframes checkmark {
    0% {
      opacity: 0;
      transform: rotate(-45deg) scale(0.8);
    }
    100% {
      opacity: 1;
      transform: rotate(-45deg) scale(1);
    }
  }
  &:last-child {
    margin-bottom: 0;
  }
`;
const ProductContainer = styled.main`
  flex: 3;
  // padding: 24px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: auto;
  position: relative;
  // &::before {
  //   content: '';
  //   position: absolute;
  //   top: 0;
  //   left: 0;
  //   right: 0;
  //   height: 4px;
  //   background: linear-gradient(to right, #1F509A, #000055);
  //   border-radius: 16px 16px 0 0;
  // }
`;
const ProductHeader = styled.h2`
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 0;
  color: #1F509A;
  position: relative;
  display: inline-block;
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 40%;
    height: 3px;
    background: linear-gradient(to right, #FFD700, #FFA500);
    border-radius: 3px;
  }
`;
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
  margin-top: 20px;
  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
  @media (min-width: 1536px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
`;
const ProductCard = styled.div`
  padding: 0;
  background: white;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  height: 100%;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #1F509A, #000055);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  &:hover::after {
    transform: scaleX(1);
  }
`;
const ImageWrapper = styled.div`
  position: relative;
  height: 220px;
  overflow: hidden;
  // background-color: #f8f9fa;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 80%, rgba(0, 0, 0, 0.05));
    z-index: 1;
    pointer-events: none;
  }
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: all 0.5s ease;
    z-index: 0;
    &:first-child {
      opacity: 1;
    }
    &:nth-child(2) {
      opacity: 0;
    }
  }
  &:hover img:first-child {
    opacity: 0;
    transform: scale(1.05);
  }
  &:hover img:nth-child(2) {
    opacity: 1;
    transform: scale(1.05);
  }
`;
const DiscountLabel = styled.span`
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #1F509A;
  padding: 6px 10px;
  border-radius: 20px;
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.85rem;
  font-weight: bold;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 4px;
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: #1F509A;
    border-radius: 50%;
    transition: background-color 0.3s ease;
  }
`;
const WishlistButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    background-color: ${props => props.isInWishlist ? '#ffe6e6' : 'white'};
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  color: #94a3b8;
  &:hover {
    transform: scale(1.1);
    color: #ef4444;
  }
  svg {
    width: 20px;
    height: 20px;
  }
  .loading-spinner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(0, 0, 122, 0.3);
    border-radius: 50%;
    border-top-color: #1F509A;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const QuickViewButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 122, 0.8);
  color: white;
  border: none;
  padding: 8px 0;
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 10;
  cursor: pointer;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  svg {
    width: 16px;
    height: 16px;
  }
  ${ImageWrapper}:hover + & {
    transform: translateY(0);
  }
  &:hover {
    background-color: rgba(0, 0, 122, 0.9);
    transform: translateY(0);
  }
`;
const ProductContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border-bottom: 3px solid #1F509A;
`;
const Title = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1F509A;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  flex: 1;
  min-width: 0;
  margin-right: 4px;
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;
const Description = styled.p`
  color: #64748b;
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  height: 2.4em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;
const Price = styled.div`
  margin-top: auto;
  padding-top: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ColorOptions = styled.div`
  margin: 0;
  display: flex;
  gap: 6px;
`;
const ColorDot = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
  position: relative;
  &:hover {
    transform: scale(1.2);
    border: 2px solid #FFD700
  }
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    border: 1px solid transparent;
    transition: all 0.2s ease;
  }
  &:hover::after {
    border-color: #e2e8f0;
  }
`;
const FilterCountBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: #FFD700;
  color: #1F509A;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const SaleTimerContainer = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #FF0000;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
  box-shadow: none;
  white-space: nowrap;
  z-index: 10;
`;

const TimerText = styled.span`
  font-size: 0.55rem;
  margin-right: 3px;
`;

const TimerNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 0.6rem;
`;

const TimerUnit = styled.span`
  display: flex;
  align-items: center;
  gap: 1px;
`;

const TimerValue = styled.span`
  font-size: 0.6rem;
  line-height: 1;
`;

const TimerLabel = styled.span`
  font-size: 0.5rem;
  opacity: 0.9;
  text-transform: lowercase;
`;

const SaleEndedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;

// Countdown Timer Component
const SaleCountdownTimer = ({ saleEndTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endTime = new Date(saleEndTime).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({
          days,
          hours,
          minutes,
          seconds,
          isExpired: false
        });
      } else {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true
        });
      }
    };

    // Calculate immediately
    calculateTimeLeft();

    // Set up interval to update every second
    const timer = setInterval(calculateTimeLeft, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, [saleEndTime]);

  if (timeLeft.isExpired) {
    // return (
    //   //<SaleTimerContainer>
    //    // <SaleEndedBadge>
    //    //   Sale Ended
    //    // </SaleEndedBadge>
    //  // </SaleTimerContainer>
    // );
  }

  return (
    <SaleTimerContainer>
      <TimerText>Ends in</TimerText>
      <TimerNumbers>
        {timeLeft.days > 0 && (
          <>
            <TimerUnit>
              <TimerValue>{String(timeLeft.days).padStart(2, '0')}</TimerValue>
              <TimerLabel>d</TimerLabel>
            </TimerUnit>
            <span>:</span>
          </>
        )}
        <TimerUnit>
          <TimerValue>{String(timeLeft.hours).padStart(2, '0')}</TimerValue>
          <TimerLabel>h</TimerLabel>
        </TimerUnit>
        <span>:</span>
        <TimerUnit>
          <TimerValue>{String(timeLeft.minutes).padStart(2, '0')}</TimerValue>
          <TimerLabel>m</TimerLabel>
        </TimerUnit>
        <span>:</span>
        <TimerUnit>
          <TimerValue>{String(timeLeft.seconds).padStart(2, '0')}</TimerValue>
          <TimerLabel>s</TimerLabel>
        </TimerUnit>
      </TimerNumbers>
    </SaleTimerContainer>
  );
};

const EyeglassesShop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInWishlist, toggleWishlist } = useWishlist();
  // Add a default value when location.state is null
  const { message = 'Eyeglasses', key = 'ca_id', table = 'getallproduct', value = 10 } = location.state || {};
  // State management
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState(value);
  const [loading, setLoading] = useState(true);
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedUsage, setSelectedUsage] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [selectedFrames, setSelectedFrames] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState([]);
  const [brandValues, setBrandValues] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]); // State for current price range
  const [productsPriceRange, setProductsPriceRange] = useState([0, 0]); // State for min/max price of products
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false); // Track if price filter is being used
  const [materials, setMaterials] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [frameSizes, setFrameSizes] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  // Expansion states
  const [isGenderExpanded, setIsGenderExpanded] = useState(false);
  const [isUsageExpanded, setIsUsageExpanded] = useState(false);
  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = auth_service.isAuthenticated();
      setIsLoggedIn(isAuth);
      if (isAuth) {
        try {
          const userData = auth_service.getAccessTokenData();
          if (userData && userData.id) {
            setCustomerId(userData.id);
          }
        } catch (error) {
          console.error("Error getting user data:", error);
        }
      }
    };
    checkAuth();
  }, []);
  // Function to toggle wishlist status
  const handleWishlistToggle = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product || !product.pr_id) return;
    const productId = product.pr_id;
    setWishlistLoading(prev => ({ ...prev, [productId]: true }));
    try {
      const productData = {
        product_id: productId,
        ca_id: product.ca_id || 10,
        name: product.parent_product_name || product.name || product.product_name,
        price: product.pr_sprice || product.price,
        pr_image: product.pr_image || product.image,
        pr_image_json: product.pr_image_json,
        slug: product.slug
      };
      const result = await toggleWishlist(productData);
      if (result.success) {
        toast.success(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
          }
        });
      } else {
        toast.error(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: '#fff',
          }
        });
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      toast.error("Error updating wishlist", {
        duration: 2000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        }
      });
    } finally {
      setWishlistLoading(prev => ({ ...prev, [productId]: false }));
    }
  };
  const [isBrandsExpanded, setIsBrandsExpanded] = useState(false);
  const [isSizeExpanded, setIsSizeExpanded] = useState(false);
  const [isMaterialExpanded, setIsMaterialExpanded] = useState(false);
  const [isPriceExpanded, setIsPriceExpanded] = useState(false);
  const [isFrameExpanded, setIsFrameExpanded] = useState(false);
  // Constants
  const allColors = {
    black: "#000",
    white: "#fff",
    brown: "#964B00",
    grey: "#808080",
    purple: "#A020F0",
    green: "#008000",
    pink: "#FFC0CB",
    blue: "#4169E1",
    red: "#FF2800",
    yellow: "#FFFF00",
  };
  const gender = [
    { gr_id: 1, bd_name: "Men" },
    { gr_id: 2, bd_name: "Women" },
    { gr_id: 5, bd_name: "Kids" },
    { gr_id: 6, bd_name: "All" },
  ];
  const frames = [
    { fr_id: 1, fr_name: "Rectangle" },
    { fr_id: 2, fr_name: "Round" },
    { fr_id: 3, fr_name: "Square" },
    { fr_id: 4, fr_name: "Rimless " },
    { fr_id: 5, fr_name: "Half Frame " },
  ];
  // Utility functions
  const getSimilarProductIconColor = (relatedProduct) => {
    // First check if the product has a specific color field
    if (relatedProduct.color && relatedProduct.color !== "") {
      return relatedProduct.color;
    }
    // Check if product has frame_color field
    if (relatedProduct.frame_color && relatedProduct.frame_color !== "") {
      return relatedProduct.frame_color;
    }
    // Fallback to slug-based color detection
    if (!relatedProduct.slug) return "#000000";
    const colorKeys = Object.keys(allColors);
    const lowerSlug = relatedProduct.slug.toLowerCase();
    for (const color of colorKeys) {
      if (lowerSlug.includes(color)) {
        return allColors[color];
      }
    }
    return "#000000";
  };

  // Calculate the position on the slider based on price (NEW VERSION)
  const calculateSliderPosition = (price) => {
    const minPrice = productsPriceRange[0];
    const maxPrice = productsPriceRange[1];

    // Handle edge case where min and max are the same
    if (minPrice === maxPrice) {
        return minPrice === 0 ? 0 : 100; // If all prices are 0, position at 0. Otherwise, position at 100%.
    }

    // Calculate percentage within the dynamic product price range
    const percentage = ((price - minPrice) / (maxPrice - minPrice)) * 100;

    // Clamp the percentage between 0 and 100
    return Math.min(Math.max(percentage, 0), 100);
  };

  // API calls
  const fetchFilteredProducts = async (message, key, value, table) => {
    // Create filters array only if both key and value are provided
    let filters = [];
    console.log('fetchFilteredProducts called with:', { message, key, value, table });
    if (key && value) {
      filters = [{ key: key, value: value }];
    }
    console.log('Filters being sent to API:', filters);
    try {
      const data = await apiService.fetchProductsWithFilters(
        filters,
        0,
        0,
        table || 'getallproduct' // Ensure table has a default value
      );
      console.log('API response data:', data);
      console.log('Number of products returned:', data?.data?.length || 0);
      setProducts(
        data.data.map((product) => ({ ...product, selectedProduct: product }))
      );
      return data;
    } catch (error) {
      console.error("Error fetching filtered products:", error);
      return null;
    }
  };
  // Reset all filters when route changes (category changes)
  useEffect(() => {
    // Reset all filter states
    setSelectedGender([]);
    setSelectedBrands([]);
    setSelectedFrames([]);
    setSelectedSize([]);
    setSelectedUsage([]);
    setSelectedMaterial([]);
    setIsPriceFilterActive(false);
    
    // Reset price range to default
    setPriceRange([0, 0]);
    setProductsPriceRange([0, 0]);
  }, [location.pathname, location.state?.message]);

  // Update price ranges when products change
  useEffect(() => {
    if (products.length > 0) {
      const prices = products.map(p => parseFloat(p.pr_sprice) || 0);
      const minPrice = 0; // Always start from 0
      const maxPrice = Math.max(...prices);

      // Round up max price slightly for better UX if needed
      const roundedMax = maxPrice > 0 ? Math.ceil(maxPrice / 10) * 10 : 100;

      // Update the range of prices found in products
      const newPriceRange = [minPrice, roundedMax];
      setProductsPriceRange(newPriceRange);

      // Only update price range if price filter is not active
      if (!isPriceFilterActive) {
        setPriceRange(newPriceRange);
      }
    } else {
        // If no products, reset ranges
        setProductsPriceRange([0, 0]);
        setPriceRange([0, 0]);
    }
  }, [products]); // Depend only on products

  // Effects
  useEffect(() => {
    // Use the default values from the destructuring
    const { message = 'Eyeglasses', key = 'ca_id', table = 'getallproduct', value = 10 } = location.state || {};
    setLoading(true);
    console.log('Location state changed:', location.state);
    console.log('Destructured values:', { message, key, value, table });
    // Reset filter state when location changes
    setFilter(value);
    // Reset all other filter states
    setSelectedGender([]);
    setSelectedBrands([]);
    setSelectedSize([]);
    setSelectedUsage([]);
    setSelectedFrames([]);
    setSelectedMaterial([]);
    // priceRange will be reset by the useEffect above when products change
    Promise.all([
      fetchFilteredProducts(message, key, value, table),
      fetchBrands(),
      fetchshape(),
      fetchMaterials()
    ]).finally(() => {
      setTimeout(() => {
        setLoading(false);
      }, 800); // Add a small delay to make the skeleton visible
    });
  }, [location]); // Remove filter dependency to prevent conflicts
    const fetchMaterials = async () => {
      try {
        const response = await apiService.fetchMaterials();
        if (response.success && Array.isArray(response.data)) {
          const filtered = response.data
            .map((item) => item.pr_material)
            .filter((m) => m !== null);
          setMaterials(filtered);
        }
      } catch (error) {
        console.error("Failed to fetch materials:", error);
      }}
       const fetchshape = async () => {
      try {
        const response = await apiService.fetchshape();
        if (response.success) {
          // Set shapes
          if (Array.isArray(response.data?.shapes)) {
            const filteredShapes = response.data.shapes
              .map((item) => item.shape)
              .filter((m) => m !== null);
            setShapes(filteredShapes);
          }
          // Set frame sizes from the API response
          if (Array.isArray(response.data?.frameSizes)) {
            const filteredSizes = response.data.frameSizes
              .map((item) => item.frame_size)
              .filter((m) => m !== null && m.trim() !== '');
            setFrameSizes(filteredSizes);
          }
        }
      } catch (error) {
        console.error("Failed to fetch materials:", error);
      }}
      const fetchBrands = async () => {
    const filters = filter ? [{ key: key, value: value }] : [];
    if (key === null) {
      filters.length = 0;
    }
    try {
      const data = await apiService.fetchBrandssById(value);
      setBrandValues(data.data);
      return data;
    } catch (error) {
      console.error("Error fetching brands:", error);
      return null;
    }
  };
  // Event handlers
  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };
  const handleColorSelect = (productIndex, selectedSlug) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const product = updatedProducts[productIndex];
      const selectedRelatedProduct = product.relatedProducts.find(
        (item) => item.slug === selectedSlug
      );
      if (selectedRelatedProduct) {
        updatedProducts[productIndex] = {
          ...product,
          selectedProduct: selectedRelatedProduct,
        };
      }
      return updatedProducts;
    });
  };
  const handleGenderChange = (brand, id) => {
    setSelectedGender((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };
  const handleBrandChange = (brand, id) => {
    setSelectedBrands((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };
  const handleFrameChange = (frameName) => {
    setSelectedFrames((prev) =>
      prev.includes(frameName)
        ? prev.filter((item) => item !== frameName)
        : [...prev, frameName]
    );
  };
  const handleSizeChange = (size) => {
    const normalizedSize = size.toString().toLowerCase(); // Normalize input
    setSelectedSize((prev) =>
      prev.includes(normalizedSize)
        ? prev.filter((item) => item !== normalizedSize)
        : [...prev, normalizedSize]
    );
    console.log("Selected sizes:", [...selectedSize, normalizedSize]); // Debug log
  };
  const handleUsageChange = (dispo) => {
    setSelectedUsage((prev) =>
      prev.includes(dispo)
        ? prev.filter((item) => item !== dispo)
        : [...prev, dispo]
    );
  };
  const handleMaterialChange = (material) => {
    setSelectedMaterial((prev) =>
      prev.includes(material)
        ? prev.filter((item) => item !== material)
        : [...prev, material]
    );
  };
  const handlePriceChange = (newRange) => {
    // Ensure the new range is within the products' price bounds
    const clampedMin = Math.max(productsPriceRange[0], newRange[0]);
    const clampedMax = Math.min(productsPriceRange[1], newRange[1]);
    setPriceRange([clampedMin, clampedMax]);
    setIsPriceFilterActive(true); // Mark price filter as active when user interacts with it
  };
  const handleResetFilters = () => {
    setSelectedGender([]);
    setSelectedFrames([]);
    setSelectedBrands([]);
    setSelectedUsage([]);
    setSelectedSize([]);
    setSelectedMaterial([]);
    // Reset price range to the full range of current products
    setPriceRange([productsPriceRange[0], productsPriceRange[1]]);
    setIsPriceFilterActive(false); // Reset price filter active state
    setIsGenderExpanded(false);
    setIsUsageExpanded(false);
    setIsFrameExpanded(false);
    setIsBrandsExpanded(false);
    setIsSizeExpanded(false);
    setIsMaterialExpanded(false);
    closeDrawer();
  };
  const handleCardClick = (product) => {
    // Add to recently viewed before navigating
    addToRecentlyViewed(product);
    navigate(`/product/${product.pr_id}/${product.ca_id}`, {
      state: { product },
    });
  };
  // Filter products based on selected criteria
  const filteredProducts = products.filter((product) => {
    // Get current route parameters
    const { message = 'Eyeglasses', key = 'ca_id', value = 10 } = location.state || {};
    // Debug logging for Flash Sale
    if (message === "Flash Sale") {
      console.log('Flash Sale filtering - Product:', product.pr_id, 'on_sale:', product.on_sale);
    }
    // For Flash Sale, ensure we only show products with on_sale = 1
    const matchesFlashSale = message === "Flash Sale" ? product.on_sale === 1 : true;
    const genderIds =
      product.sg_gender_ids?.split(",").map((id) => id.trim()) || [];
    const matchesGender =
      selectedGender.length === 0 ||
      selectedGender.some((gender) => genderIds.includes(gender.toString()));
    // Only apply price filter if it's been actively used by the user
    const matchesPrice = !isPriceFilterActive || 
      ((parseFloat(product.pr_sprice) || 0) >= priceRange[0] && 
       (parseFloat(product.pr_sprice) || 0) <= priceRange[1]);
    const matchesBrand =
      selectedBrands.length === 0 || selectedBrands.includes(product.br_id);
    const matchesFrame =
      selectedFrames.length === 0 ||
      selectedFrames.includes(product.shape);
      const productFrameSize = product.frame_size?.toString().toLowerCase() || product.size?.toString().toLowerCase();
      const matchesSize =
        selectedSize.length === 0 ||
        (productFrameSize && selectedSize.includes(productFrameSize));
      const matchesUsage =
  selectedUsage.length === 0 ||
  selectedUsage.some(usage => product.pr_description.toLowerCase().includes(usage.toLowerCase()));
    const matchesMaterial =
      selectedMaterial.length === 0 ||
      selectedMaterial.includes(product.pr_material);
    return (
      matchesFlashSale &&
      matchesGender &&
      matchesBrand &&
      matchesSize &&
      matchesUsage &&
      matchesFrame &&
      matchesPrice &&
      matchesMaterial
    );
  });
  // Debug logging for Flash Sale filtering results
  const currentMessage = location.state?.message || 'Eyeglasses';
  if (currentMessage === "Flash Sale") {
    console.log('Flash Sale filtering results:');
    console.log('Total products before filtering:', products.length);
    console.log('Products with on_sale = 1:', products.filter(p => p.on_sale === 1).length);
    console.log('Final filtered products:', filteredProducts.length);
  }
  // Sort filtered products based on selected criteria
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    // Access the selectedProduct object which contains the actual product data
    const productA = a.selectedProduct || a;
    const productB = b.selectedProduct || b;
    switch (sortBy) {
      case "price-low":
        return parseFloat(productA.pr_price || 0) - parseFloat(productB.pr_price || 0);
      case "price-high":
        return parseFloat(productB.pr_price || 0) - parseFloat(productA.pr_price || 0);
      case "newest":
        // Try to use created_at if available, otherwise fall back to pr_id (assuming newer products have higher IDs)
        const dateA = productA.created_at ? new Date(productA.created_at) : new Date(0);
        const dateB = productB.created_at ? new Date(productB.created_at) : new Date(0);
        if (dateA.getTime() === dateB.getTime()) {
          // If dates are the same or both invalid, use product ID as fallback
          return parseInt(productB.pr_id || 0) - parseInt(productA.pr_id || 0);
        }
        return dateB - dateA;
      case "bestselling":
        return parseInt(productB.sales_count || 0) - parseInt(productA.sales_count || 0);
      case "featured":
      default:
        return 0; // Keep original order for featured
    }
  });
  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };
  // Count selected filters
  const selectedFiltersCount =
    selectedGender.length +
    selectedBrands.length +
    selectedFrames.length +
    selectedSize.length +
    selectedUsage.length +
    selectedMaterial.length +
    (priceRange[0] > productsPriceRange[0] || priceRange[1] < productsPriceRange[1] ? 1 : 0); // Check against product range
  // Filter content component to avoid repetition
  const FilterContent = () => {
    return (
    <>
      <FilterHeader>
        <div className="flex items-center justify-between w-full">
          {selectedFiltersCount > 0 ? (
            <span className="text-sm text-gray-600 font-medium">
              {selectedFiltersCount} {selectedFiltersCount === 1 ? 'filter' : 'filters'} applied
            </span>
          ) : (
            <span className="text-sm text-gray-600">No filters applied</span>
          )}
          {selectedFiltersCount > 0 && (
            <ResetButton onClick={handleResetFilters}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </ResetButton>
          )}
        </div>
      </FilterHeader>
      {message === "Contact lenses" ? (
        <>
          <FilterSection>
            <FilterToggle onClick={() => setIsPriceExpanded(!isPriceExpanded)}>
              <h3>
                <span className="text-lg font-semibold mr-2">₹</span>
                Price Range
              </h3>
              <ToggleButton>{isPriceExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isPriceExpanded && (
              <FilterOptions>
                <div className="flex justify-between items-center mb-4 px-2">
                  <div className="px-3 py-2 bg-[#f0f4ff] rounded-md border border-[#e0e7ff] shadow-sm">
                    <span className="text-[#1F509A] font-semibold">₹{priceRange[0]}</span>
                  </div>
                  <div className="text-gray-500 font-medium">to</div>
                  <div className="px-3 py-2 bg-[#f0f4ff] rounded-md border border-[#e0e7ff] shadow-sm">
                    <span className="text-[#1F509A] font-semibold">₹{priceRange[1]}</span>
                  </div>
                </div>
                <div className="px-2">
                  {/* Dual Range Slider */}
                  <div className="relative mb-6">
                    {/* Slider Track - Fixed width visually */}
                    <div className="relative w-full h-2 bg-gray-200 rounded-full mt-4">
                      {/* Active range - Positioned based on calculated percentage */}
                      <div
                        className="absolute h-2 bg-[#1F509A] rounded-full"
                        style={{
                          left: `${calculateSliderPosition(priceRange[0])}%`,
                          width: `${calculateSliderPosition(priceRange[1]) - calculateSliderPosition(priceRange[0])}%`
                        }}
                      ></div>
                      {/* Min thumb - Positioned based on calculated percentage */}
                      <div
                        style={{ left: `calc(${calculateSliderPosition(priceRange[0])}% - 10px)` }}
                        className="absolute top-1/2 w-5 h-5 bg-white border-2 border-[#1F509A] rounded-full -mt-2.5 shadow-lg cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-xl"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent default to avoid text selection
                          const startX = e.clientX;
                          const startValue = priceRange[0];
                          const trackElement = e.currentTarget.parentElement;
                          const trackRect = trackElement.getBoundingClientRect();
                          const trackWidth = trackRect.width;

                          const handleMouseMove = (e) => {
                            const deltaX = e.clientX - startX;
                            // Calculate new percentage position (0-100)
                            const newPercentage = Math.max(0, Math.min(100, calculateSliderPosition(startValue) + (deltaX / trackWidth) * 100));
                            // Map percentage back to price value within the products' range
                            const minPrice = productsPriceRange[0];
                            const maxPrice = productsPriceRange[1];
                            const newValueRaw = minPrice + (newPercentage / 100) * (maxPrice - minPrice);
                            const newValue = Math.round(newValueRaw / 10) * 10; // Round to nearest 10

                            // Ensure min thumb doesn't go beyond max thumb
                            if (newValue < priceRange[1]) {
                              handlePriceChange([newValue, priceRange[1]]);
                            }
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };

                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                      ></div>
                      {/* Max thumb - Positioned based on calculated percentage */}
                      <div
                        style={{ left: `calc(${calculateSliderPosition(priceRange[1])}% - 10px)` }}
                        className="absolute top-1/2 w-5 h-5 bg-white border-2 border-[#1F509A] rounded-full -mt-2.5 shadow-lg cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-xl"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startValue = priceRange[1];
                          const trackElement = e.currentTarget.parentElement;
                          const trackRect = trackElement.getBoundingClientRect();
                          const trackWidth = trackRect.width;

                          const handleMouseMove = (e) => {
                            const deltaX = e.clientX - startX;
                            const newPercentage = Math.max(0, Math.min(100, calculateSliderPosition(startValue) + (deltaX / trackWidth) * 100));
                            const minPrice = productsPriceRange[0];
                            const maxPrice = productsPriceRange[1];
                            const newValueRaw = minPrice + (newPercentage / 100) * (maxPrice - minPrice);
                            const newValue = Math.round(newValueRaw / 10) * 10; // Round to nearest 10

                            // Ensure max thumb doesn't go before min thumb
                            if (newValue > priceRange[0]) {
                              handlePriceChange([priceRange[0], newValue]);
                            }
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };

                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                      ></div>
                    </div>
                    {/* Invisible range slider for clicking on track */}
                    <div
                      className="absolute top-0 left-0 w-full h-6 cursor-pointer dual-range-container"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const trackElement = e.currentTarget;
                        const rect = trackElement.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const clickPercentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));

                        const minPrice = productsPriceRange[0];
                        const maxPrice = productsPriceRange[1];
                        const clickedValueRaw = minPrice + (clickPercentage / 100) * (maxPrice - minPrice);
                        const clickedValue = Math.round(clickedValueRaw / 10) * 10; // Round to nearest 10

                        // Determine which handle is closer
                        const distToMin = Math.abs(clickedValue - priceRange[0]);
                        const distToMax = Math.abs(clickedValue - priceRange[1]);
                        const isMin = distToMin <= distToMax;

                        if (isMin && clickedValue <= priceRange[1] - 10) {
                          handlePriceChange([clickedValue, priceRange[1]]);
                        } else if (!isMin && clickedValue >= priceRange[0] + 10) {
                          handlePriceChange([priceRange[0], clickedValue]);
                        }
                      }}
                      style={{ zIndex: 10 }}
                    />
                    {/* Price range labels - Use products' min/max for display */}
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>₹{productsPriceRange[0]}</span>
                      <span>₹{Math.round((productsPriceRange[0] + productsPriceRange[1]) / 2)}</span>
                      <span>₹{productsPriceRange[1]}</span>
                    </div>
                  </div>
                  {/* Quick preset buttons */}
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button
                      className="px-3 py-2 text-xs bg-gray-100 hover:bg-[#1F509A] hover:text-white rounded-md transition-colors duration-200 border border-gray-200"
                      onClick={() => handlePriceChange([productsPriceRange[0], Math.max(productsPriceRange[0] + 500, productsPriceRange[1])])}
                    >
                      ₹{productsPriceRange[0]} - ₹{Math.min(productsPriceRange[0] + 500, productsPriceRange[1])}
                    </button>
                    <button
                      className="px-3 py-2 text-xs bg-gray-100 hover:bg-[#1F509A] hover:text-white rounded-md transition-colors duration-200 border border-gray-200"
                      onClick={() => handlePriceChange([Math.max(productsPriceRange[0], productsPriceRange[1] - 500), productsPriceRange[1]])}
                    >
                      ₹{Math.max(productsPriceRange[0], productsPriceRange[1] - 500)} - ₹{productsPriceRange[1]}
                    </button>
                     <button
                      className="px-3 py-2 text-xs bg-gray-100 hover:bg-[#1F509A] hover:text-white rounded-md transition-colors duration-200 border border-gray-200 col-span-2"
                      onClick={() => handlePriceChange([productsPriceRange[0], productsPriceRange[1]])}
                    >
                      Full Range (₹{productsPriceRange[0]} - ₹{productsPriceRange[1]})
                    </button>
                  </div>
                  <button
                    className="w-full mt-3 px-3 py-2 text-sm bg-gray-100 hover:bg-red-500 hover:text-white rounded-md transition-colors duration-200 border border-gray-200"
                    onClick={() => handlePriceChange([productsPriceRange[0], productsPriceRange[1]])} // Reset to full range
                  >
                    Clear Filter
                  </button>
                </div>
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle onClick={() => setIsUsageExpanded(!isUsageExpanded)}>
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Replacement Cycle
              </h3>
              <ToggleButton>{isUsageExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isUsageExpanded && (
              <FilterOptions>
                {[
                  "Daily",
                  "Weekly",
                  "Monthly",
                  "Yearly"
                ].map((dispo) => (
                  <CheckboxLabel key={dispo}>
                    <div
                      className={`custom-checkbox ${
                        selectedUsage.includes(dispo) ? "checked" : ""
                      }`}
                    />
                    <input
                      type="checkbox"
                      checked={selectedUsage.includes(dispo)}
                      onChange={() => handleUsageChange(dispo)}
                    />
                    <span>{dispo}</span>
                  </CheckboxLabel>
                ))}
              </FilterOptions>
            )}
          </FilterSection>
        </>
      ) : (
        <>
          <FilterSection>
            <FilterToggle
              onClick={() => setIsGenderExpanded(!isGenderExpanded)}
            >
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Gender
                {selectedGender.length > 0 && (
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    {selectedGender.length}
                  </span>
                )}
              </h3>
              <ToggleButton>{isGenderExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isGenderExpanded && (
              <FilterOptions>
                <div className="grid grid-cols-2 gap-2">
                  {gender.map((brand) => (
                    <CheckboxLabel key={brand.gr_id}>
                      <div
                        className={`custom-checkbox ${
                          selectedGender.includes(brand.gr_id) ? "checked" : ""
                        }`}
                      />
                      <input
                        type="checkbox"
                        checked={selectedGender.includes(brand.gr_id)}
                        onChange={() =>
                          handleGenderChange(brand.bd_name, brand.gr_id)
                        }
                      />
                      <span>{brand.bd_name}</span>
                    </CheckboxLabel>
                  ))}
                </div>
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle
              onClick={() => setIsBrandsExpanded(!isBrandsExpanded)}
            >
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Brands
                {selectedBrands.length > 0 && (
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    {selectedBrands.length}
                  </span>
                )}
              </h3>
              <ToggleButton>{isBrandsExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isBrandsExpanded && (
              <FilterOptions>
                {brandValues.length > 0 ? (
                  <div className="space-y-2">
                    {brandValues.map((brand) => (
                      <CheckboxLabel key={brand.bd_id}>
                        <div
                          className={`custom-checkbox ${
                            selectedBrands.includes(brand.bd_id) ? "checked" : ""
                          }`}
                        />
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand.bd_id)}
                          onChange={() =>
                            handleBrandChange(brand.bd_name, brand.bd_id)
                          }
                        />
                        <span>{brand.bd_name}</span>
                      </CheckboxLabel>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-gray-500">No brands available</div>
                )}
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle onClick={() => setIsFrameExpanded(!isFrameExpanded)}>
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 5v2a1 1 0 001 1h2a1 1 0 001 1v2a1 1 0 01-1 1H10a1 1 0 01-1-1V7a1 1 0 001-1h2m-2 3H8v3a1 1 0 001 1h3V10a1 1 0 001 1h2a1 1 0 001 1v3a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1h-2a1 1 0 00-1 1v3H8a1 1 0 001 1h2" />
                </svg>
                Frame Shape
                {selectedFrames.length > 0 && (
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    {selectedFrames.length}
                  </span>
                )}
              </h3>
              <ToggleButton>{isFrameExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isFrameExpanded && (
              <FilterOptions>
                {shapes.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {shapes.map((frame) => (
                      <CheckboxLabel key={frame}>
                        <div
                          className={`custom-checkbox ${
                            selectedFrames.includes(frame) ? "checked" : ""
                          }`}
                        />
                        <input
                          type="checkbox"
                          checked={selectedFrames.includes(frame)}
                          onChange={() => handleFrameChange(frame)}
                        />
                        <span title={frame}> {/* Add title attribute for tooltip content */}
              <div className="tooltip">{frame}</div> {/* Tooltip content */}
              {frame}
            </span>
                      </CheckboxLabel>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-gray-500">No frame shapes available</div>
                )}
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle onClick={() => setIsSizeExpanded(!isSizeExpanded)}>
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Frame Size
                {selectedSize.length > 0 && (
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    {selectedSize.length}
                  </span>
                )}
              </h3>
              <ToggleButton>{isSizeExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isSizeExpanded && (
              <FilterOptions>
                <div className="grid grid-cols-2 gap-2">
                  {frameSizes.length > 0 ? frameSizes.map((size) => (
                    <CheckboxLabel key={size}>
                      <div
                        className={`custom-checkbox ${
                          selectedSize.includes(size) ? "checked" : ""
                        }`}
                      />
                      <input
                        type="checkbox"
                        checked={selectedSize.includes(size)}
                        onChange={() => handleSizeChange(size)}
                        className="sr-only"
                        aria-label={`Filter by size ${size}`}
                      />
                      <span title={size}> {/* Add title attribute for tooltip content */}
              <div className="tooltip">{size}</div> {/* Tooltip content */}
              {size}
            </span>
                    </CheckboxLabel>
                  )) : (
                    <div className="text-center py-3 text-gray-500 col-span-2">
                      No frame sizes available
                    </div>
                  )}
                </div>
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle
              onClick={() => setIsMaterialExpanded(!isMaterialExpanded)}
            >
              <h3>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                Material
                {selectedMaterial.length > 0 && (
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    {selectedMaterial.length}
                  </span>
                )}
              </h3>
              <ToggleButton>{isMaterialExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isMaterialExpanded && (
              <FilterOptions>
                {materials.length > 0 ? (
                  <div className="space-y-2">
                    {materials.map((material) => (
                      <CheckboxLabel key={material}>
                        <div
                          className={`custom-checkbox ${
                            selectedMaterial.includes(material) ? "checked" : ""
                          }`}
                        />
                        <input
                          type="checkbox"
                          checked={selectedMaterial.includes(material)}
                          onChange={() => handleMaterialChange(material)}
                        />
                        <span title={material}> {/* Add title attribute for tooltip content */}
              <div className="tooltip">{material}</div> {/* Tooltip content */}
              {material}
            </span>
                      </CheckboxLabel>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-gray-500">No materials available</div>
                )}
              </FilterOptions>
            )}
          </FilterSection>
          <FilterSection>
            <FilterToggle onClick={() => setIsPriceExpanded(!isPriceExpanded)}>
              <h3>
                <span className="text-lg font-semibold mr-2">₹</span>
                Price Range
                {(priceRange[0] > productsPriceRange[0] || priceRange[1] < productsPriceRange[1]) && ( // Check against product range
                  <span className="ml-2 bg-[#1F509A] text-white text-xs px-2 py-1 rounded-full">
                    1
                  </span>
                )}
              </h3>
              <ToggleButton>{isPriceExpanded ? "−" : "+"}</ToggleButton>
            </FilterToggle>
            {isPriceExpanded && (
              <FilterOptions>
                <div className="flex justify-between items-center mb-6 bg-[#f8f9fa] p-4 rounded-lg border border-[#e2e8f0]">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-500 text-xs mb-1">Min</span>
                    <div className="px-4 py-2 bg-white rounded-md border border-[#1F509A] shadow-sm">
                      <span className="text-[#1F509A] font-semibold">₹{priceRange[0]}</span>
                    </div>
                  </div>
                  <div className="text-[#1F509A]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-gray-500 text-xs mb-1">Max</span>
                    <div className="px-4 py-2 bg-white rounded-md border border-[#1F509A] shadow-sm">
                      <span className="text-[#1F509A] font-semibold">₹{priceRange[1]}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-8 relative">
                  {/* Slider Track - Fixed width visually */}
                  <div className="h-2 bg-gray-200 rounded-full mb-6 relative">
                    {/* Active range - Positioned based on calculated percentage */}
                    <div
                      style={{
                        left: `${calculateSliderPosition(priceRange[0])}%`,
                        width: `${calculateSliderPosition(priceRange[1]) - calculateSliderPosition(priceRange[0])}%`
                      }}
                      className="absolute top-0 h-full bg-[#1F509A] rounded-full"
                    ></div>
                    {/* Min thumb - Positioned based on calculated percentage */}
                    <div
                      style={{ left: `${calculateSliderPosition(priceRange[0])}%` }}
                      className="absolute top-1/2 w-5 h-5 bg-white border-2 border-[#1F509A] rounded-full -mt-2.5 -ml-2.5 shadow-md"
                    ></div>
                    {/* Max thumb - Positioned based on calculated percentage */}
                    <div
                      style={{ left: `${calculateSliderPosition(priceRange[1])}%` }}
                      className="absolute top-1/2 w-5 h-5 bg-white border-2 border-[#1F509A] rounded-full -mt-2.5 -ml-2.5 shadow-md"
                    ></div>
                  </div>
                  {/* Interactive dual range area - Fixed width visually */}
                  <div
                    className="absolute top-0 left-0 w-full h-6 cursor-pointer dual-range-container"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const trackElement = e.currentTarget;
                      const rect = trackElement.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const clickPercentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));

                      const minPrice = productsPriceRange[0]; // Use dynamic product range
                      const maxPrice = productsPriceRange[1]; // Use dynamic product range
                      const clickedValueRaw = minPrice + (clickPercentage / 100) * (maxPrice - minPrice);
                      const clickedValue = Math.round(clickedValueRaw / 10) * 10; // Round to nearest 10

                      // Determine which handle is closer
                      const distToMin = Math.abs(clickedValue - priceRange[0]);
                      const distToMax = Math.abs(clickedValue - priceRange[1]);
                      const isMin = distToMin <= distToMax;

                      if (isMin && clickedValue <= priceRange[1] - 10) {
                        handlePriceChange([clickedValue, priceRange[1]]);
                      } else if (!isMin && clickedValue >= priceRange[0] + 10) {
                        handlePriceChange([priceRange[0], clickedValue]);
                      }
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const trackElement = e.currentTarget;
                      const rect = trackElement.getBoundingClientRect();
                      const touchX = e.touches[0].clientX - rect.left;
                      const touchPercentage = Math.max(0, Math.min(100, (touchX / rect.width) * 100));

                      const minPrice = productsPriceRange[0];
                      const maxPrice = productsPriceRange[1];
                      const touchedValueRaw = minPrice + (touchPercentage / 100) * (maxPrice - minPrice);
                      const touchedValue = Math.round(touchedValueRaw / 10) * 10; // Round to nearest 10

                      const distToMin = Math.abs(touchedValue - priceRange[0]);
                      const distToMax = Math.abs(touchedValue - priceRange[1]);
                      const isMin = distToMin <= distToMax;

                      if (isMin && touchedValue <= priceRange[1] - 10) {
                        handlePriceChange([touchedValue, priceRange[1]]);
                      } else if (!isMin && touchedValue >= priceRange[0] + 10) {
                        handlePriceChange([priceRange[0], touchedValue]);
                      }

                      const handleTouchMove = (e) => {
                        e.preventDefault();
                        const touchX = e.touches[0].clientX - rect.left;
                        const touchPercentage = Math.max(0, Math.min(100, (touchX / rect.width) * 100));

                        const movedValueRaw = minPrice + (touchPercentage / 100) * (maxPrice - minPrice);
                        const movedValue = Math.round(movedValueRaw / 10) * 10; // Round to nearest 10

                        if (isMin) {
                          if (movedValue <= priceRange[1] - 10) {
                            handlePriceChange([movedValue, priceRange[1]]);
                          }
                        } else {
                          if (movedValue >= priceRange[0] + 10) {
                            handlePriceChange([priceRange[0], movedValue]);
                          }
                        }
                      };

                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };

                      document.addEventListener('touchmove', handleTouchMove);
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                    style={{ zIndex: 10 }}
                  />
                  {/* Price range labels - Use dynamic product range */}
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹{productsPriceRange[0]}</span>
                    <span>₹{Math.round((productsPriceRange[0] + productsPriceRange[1]) / 2)}</span>
                    <span>₹{productsPriceRange[1]}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      priceRange[0] === productsPriceRange[0] && priceRange[1] <= productsPriceRange[0] + 500
                        ? 'bg-[#1F509A] text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#1F509A] hover:bg-white'
                    }`}
                    onClick={() => handlePriceChange([productsPriceRange[0], Math.min(productsPriceRange[0] + 500, productsPriceRange[1])])}
                  >
                    Under ₹{Math.min(productsPriceRange[0] + 500, productsPriceRange[1])}
                  </button>
                  <button
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      priceRange[0] >= productsPriceRange[1] - 1000 && priceRange[1] === productsPriceRange[1]
                        ? 'bg-[#1F509A] text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#1F509A] hover:bg-white'
                    }`}
                    onClick={() => handlePriceChange([Math.max(productsPriceRange[1] - 1000, productsPriceRange[0]), productsPriceRange[1]])}
                  >
                    ₹{Math.max(productsPriceRange[1] - 1000, productsPriceRange[0])}+
                  </button>
                   <button
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all col-span-2 ${
                      priceRange[0] === productsPriceRange[0] && priceRange[1] === productsPriceRange[1]
                        ? 'bg-[#1F509A] text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#1F509A] hover:bg-white'
                    }`}
                    onClick={() => handlePriceChange([productsPriceRange[0], productsPriceRange[1]])}
                  >
                    Full Range (₹{productsPriceRange[0]} - ₹{productsPriceRange[1]})
                  </button>
                </div>
                <button
                  className="w-full mt-6 px-4 py-2 bg-gray-100 text-[#1F509A] rounded-md text-sm font-medium border border-gray-200 hover:bg-white hover:border-[#1F509A] transition-colors flex items-center justify-center gap-2"
                  onClick={() => handlePriceChange([productsPriceRange[0], productsPriceRange[1]])} // Reset to full range
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Price Range
                </button>
              </FilterOptions>
            )}
          </FilterSection>
        </>
      )}
    </>
  );
  };
 return (
  <div className="bg-gray-50 min-h-screen pb-10">
    <div style={{marginBottom: "5px"}} className="bg-[#1F509A] text-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-2 text-sm py-2">
          <span>Home</span>
          <span>›</span>
          <span>Products</span>
          <span>›</span>
          <span className="text-[#FFD700]">{message}</span>
        </div>
      </div>
    </div>
    <Container>

      <DrawerOverlay isOpen={isDrawerOpen} onClick={closeDrawer} />
      <Drawer isOpen={isDrawerOpen}>
        <DrawerHeader>
          <FilterTitle>Filter Options</FilterTitle>
          <CloseButton onClick={closeDrawer}>
            <X size={24} />
          </CloseButton>
        </DrawerHeader>
        <FilterContent />
      </Drawer>
      <Sidebar>
        <FilterContent />
      </Sidebar>
      <ProductContainer>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center px-4 md:px-5">
          <div className="w-full">
            <div className="flex justify-between items-center mb-4">
              <ProductHeader>{message}</ProductHeader>
              <MobileFilterButton onClick={toggleDrawer}>
                <Filter size={18} />
                <span className="hidden sm:inline">Filter</span>
                {selectedFiltersCount > 0 && (
                  <FilterCountBadge>{selectedFiltersCount}</FilterCountBadge>
                )}
              </MobileFilterButton>
            </div>
            <p className="text-black-500 mt-4 text-base">
              {message === "Eyeglasses" ? "Discover our collection of premium eyeglasses designed for style and comfort." :
               message === "Sunglasses" ? "Discover Our Sunglasses — Made for Comfort. Built for Protection" :
               message === "Contact lenses" ? "Discover our premium collection of contact lenses—crafted for all-day comfort, crystal-clear vision, and effortless" :
               message === "Flash Sale" ? "Hurry! Deals Disappear in a Flash ⚡”" :
               message === "Genie Blu" ? "GenieBlu – The Smart Way to Screen. Advanced blue light filtering to protect your eyes from strain, dryness, and fatigue — so you stay sharp all day." :
               `Discover our collection of premium ${message.toLowerCase()} designed for style and comfort.`}
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mt-4 md:mt-0">
            {/* <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3 rounded-xl border border-blue-100 shadow-sm">
              <div className="p-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-800 text-sm font-semibold">
                  {sortedProducts.length} Products
                </span>
                <span className="text-gray-500 text-xs">
                  Found in {message}
                </span>
              </div>
            </div> */}
            <div className="flex items-center gap-2 bg-white hover:bg-gray-50 px-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 w-full md:w-auto group">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#1F509A] group-hover:text-blue-600 transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
              </svg>
              <select
                className="bg-transparent border-none text-sm focus:outline-none w-full md:w-auto font-medium text-gray-700 cursor-pointer"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>
        {/* <div className="flex flex-wrap gap-2 mb-6">
          {selectedGender.length > 0 && (
            <div className="flex items-center gap-1 bg-[#f0f4ff] text-[#1F509A] px-3 py-1 rounded-full text-sm">
              <span>Gender</span>
              <button className="ml-1 text-xs bg-[#1F509A] text-white rounded-full w-4 h-4 flex items-center justify-center">
                ×
              </button>
            </div>
          )}
          {selectedBrands.length > 0 && (
            <div className="flex items-center gap-1 bg-[#f0f4ff] text-[#1F509A] px-3 py-1 rounded-full text-sm">
              <span>Brands</span>
              <button className="ml-1 text-xs bg-[#1F509A] text-white rounded-full w-4 h-4 flex items-center justify-center">
                ×
              </button>
            </div>
          )}
          {selectedFrames.length > 0 && (
            <div className="flex items-center gap-1 bg-[#f0f4ff] text-[#1F509A] px-3 py-1 rounded-full text-sm">
              <span>Frame Shape</span>
              <button className="ml-1 text-xs bg-[#1F509A] text-white rounded-full w-4 h-4 flex items-center justify-center">
                ×
              </button>
            </div>
          )}
          {(priceRange[0] > 0 || priceRange[1] < 3000) && (
            <div className="flex items-center gap-1 bg-[#f0f4ff] text-[#1F509A] px-3 py-1 rounded-full text-sm">
              <span>Price: ₹{priceRange[0]} - ₹{priceRange[1]}</span>
              <button className="ml-1 text-xs bg-[#1F509A] text-white rounded-full w-4 h-4 flex items-center justify-center">
                ×
              </button>
            </div>
          )}
        </div> */}
        {loading ? (
          <ProductGrid>
            <SkeletonLoading type="product" count={12} />
          </ProductGrid>
        ) : filteredProducts.length > 0 ? (
          <ProductGrid>
            {sortedProducts.map((product, index) => {
              const { selectedProduct } = product;
              // Debug log for lenses only
              if (message === "Contact lenses" || selectedProduct.ca_id === 3) {
                console.log("Lens product data:", selectedProduct);
              }
              let images = [];

              if (Array.isArray(selectedProduct.pr_image_json)) {
                images = selectedProduct.pr_image_json; // already parsed
              } else if (typeof selectedProduct.pr_image_json === "string") {
                try {
                  images = JSON.parse(selectedProduct.pr_image_json);
                } catch (err) {
                  console.error("Failed to parse pr_image_json:", selectedProduct.pr_image_json, err);
                  images = [];
                }
              }
              
              const firstImage = images[0] || "https://via.placeholder.com/150";
              const secondImage = images[1] || firstImage;
              
              const showDiscount = selectedProduct.pr_price && selectedProduct.pr_price !== selectedProduct.pr_sprice;
              const discountPercent = showDiscount
                ? Math.round(((selectedProduct.pr_sprice - selectedProduct.pr_price) / selectedProduct.pr_sprice) * 100)
                : 0;
              return (
<ProductCard
  key={selectedProduct.pr_id}
  onClick={() => handleCardClick(selectedProduct)}
  className="relative p-2 rounded-lg border hover:shadow-lg transition duration-300 bg-white"
>
  {/* SALE BADGE */}
  {selectedProduct.on_sale === 1 && (
                        <DiscountLabel>Sale</DiscountLabel>
  )}
  {/* WISHLIST BUTTON */}
  <button
    onClick={(e) => handleWishlistToggle(e, selectedProduct)}
    disabled={wishlistLoading[selectedProduct.pr_id]}
    className={`absolute top-2 right-2 z-10 bg-white rounded-full p-1.5 shadow-md transition-all duration-300 hover:scale-110 ${
      wishlistLoading[selectedProduct.pr_id] ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'
    }`}
  >
    {wishlistLoading[selectedProduct.pr_id] ? (
      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={isInWishlist(selectedProduct.pr_id) ? "#EF4444" : "none"}
        viewBox="0 0 24 24"
        stroke={isInWishlist(selectedProduct.pr_id) ? "#EF4444" : "#6B7280"}
        className={`w-4 h-4 transition-all duration-300 ${
          isInWishlist(selectedProduct.pr_id) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
        }`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    )}
  </button>
  {/* IMAGE */}
  <ImageWrapper className="relative aspect-square overflow-hidden rounded-md">
    <img
      src={firstImage}
      alt={selectedProduct.parent_product_name}
      className="object-cover w-full h-full transition-opacity duration-300"
    />
    <img
      src={secondImage}
      alt="hover"
      className="absolute inset-0 object-cover w-full h-full opacity-0 hover:opacity-100 transition-opacity duration-300"
    />
    {/* SALE COUNTDOWN TIMER */}
    {selectedProduct.on_sale === 1 && selectedProduct.sale_ends_time && (
      <SaleCountdownTimer saleEndTime={selectedProduct.sale_ends_time} />
    )}
  </ImageWrapper>
  {/* CONTENT */}
  <ProductContent className="pt-2 text-sm">
    <div className="flex justify-between items-start text-xs text-gray-700 mb-1 font-medium">
      <div className="flex items-center flex-1 min-w-0">
        <Title className="truncate">{selectedProduct.parent_product_name}</Title>
      </div>
    </div>
    {/* Show product name for lenses only */}
    {(message === "Contact lenses" || selectedProduct.ca_id === 3) && (
      <div className="mb-1">
        <div className={`text-s font-bold ${selectedProduct.ca_id === 3 ? 'text-[#1F509A]' : 'text-gray-600'}`}>
          <span className="truncate">
            {selectedProduct.name || selectedProduct.product_name || selectedProduct.pr_name || selectedProduct.slug || 'Product Name'}
          </span>
        </div>
        {selectedProduct.ca_id === 3 && selectedProduct.pr_description && (
          <div className="text-xs text-gray-500 mt-1 line-clamp-2">
            {selectedProduct.pr_description}
          </div>
        )}
      </div>
    )}
    {/* Rating Display */}
    {selectedProduct.avg_rating && (
      <div className="flex items-center gap-1 mb-1">
        <StarRating
          rating={parseFloat(selectedProduct.avg_rating)}
          readonly={true}
          size={12}
        />
        <span className="text-xs text-gray-600">
          ({selectedProduct.rating_count || 0})
        </span>
      </div>
    )}
    {product.relatedProducts?.length > 0 && (
      <ColorOptions className="flex gap-1 mb-1">
        {product.relatedProducts.map((relatedProduct) => {
          const color = getSimilarProductIconColor(relatedProduct);
          return (
            <ColorDot
              key={relatedProduct.pr_id}
              color={color}
              onClick={(e) => {
                e.stopPropagation();
                handleColorSelect(index, relatedProduct.slug);
              }}
              className="w-4 h-4 rounded-full border shadow-sm"
            />
          );
        })}
      </ColorOptions>
    )}
    <Price className="flex items-center gap-2 font-bold text-sm">
      {showDiscount ? (
        <>
          <span className="text-[#1F509A]">₹{selectedProduct.pr_price}</span>
          <span className="line-through text-gray-400 text-xs">₹{selectedProduct.pr_sprice}</span>
          <span className="ml-auto bg-green-100 text-green-600 text-[10px] font-semibold px-2 py-[1px] rounded">
            {discountPercent}% off
          </span>
        </>
      ) : (
        <span className="text-[#1F509A]">₹{selectedProduct.pr_sprice}</span>
      )}
    </Price>
  </ProductContent>
</ProductCard>
              );
            })}
          </ProductGrid>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-24 h-24 mb-6 text-[#1F509A] opacity-30">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
            <p className="text-gray-500 text-center max-w-md mb-6">
              We couldn't find any products matching your current filters. Try adjusting your filter criteria.
            </p>
            <button
              onClick={handleResetFilters}
              className="px-6 py-3 bg-[#1F509A] text-white rounded-lg font-semibold hover:bg-[#000055] transition-colors"
            >
              Reset All Filters
            </button>
          </div>
        )}
      </ProductContainer>
    </Container>
  </div>
);
};
export default EyeglassesShop;
